import type { StagedActionEditTag, StagedActionView } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'

/** A multiText token: `value` is what gets submitted (and stored), `label` is shown to the user. */
export interface MultiTextItem {
  value: string
  label: string
  /**
   * A known contact (display-name view value) whose email couldn't be resolved
   * from the current options. `value` is empty, so the token is displayed but
   * never submitted. Transient: resolves once the edit-options query refetches
   * after an add/remove PATCH.
   */
  unresolved?: boolean
}

/**
 * Derive the multiText token list for an editable tag.
 *
 * The view tags are the live source of truth for which tokens exist (a
 * successful add/remove PATCH refreshes them); `options` is only a label ->
 * value lookup. For name/value-divergent tags (e.g. calendar `Guest`, where
 * each attendee is `{ name: displayName, value: email }`) the displayed label
 * and submitted value diverge, so the email is recovered from `options`.
 *
 * Deliberately stateless — a pure function of the current `options` + `tags`,
 * carrying no resolved state across renders. The action drawer reuses one
 * mounted editor across staged actions, so cross-render caching would leak one
 * action's email onto another's colliding display name. On an options miss for
 * a name/value tag we keep the view value only if it's already email-shaped (a
 * freshly-typed guest); otherwise the token is marked `unresolved` so a display
 * name is never submitted as an email and the attendee is never silently
 * dropped — edits block until the refetch resolves it.
 *
 * When two options share a display name, their emails are consumed positionally
 * (FIFO) so same-named guests resolve to distinct emails instead of collapsing.
 *
 * For plain free-text multiText (no options) the value IS the label.
 */
export function seedMultiTextItems(
  options: StagedActionEditTag['options'],
  tags: Array<Pick<StagedActionView['tags'][number], 'value'>>,
): Array<MultiTextItem> {
  const valuesByLabel = new Map<string, Array<string>>()
  for (const opt of options) {
    const queue = valuesByLabel.get(opt.name)
    if (queue) queue.push(opt.value)
    else valuesByLabel.set(opt.name, [opt.value])
  }

  const hasOptions = options.length > 0

  return tags.map((tag): MultiTextItem => {
    // Truthiness, not `!= null`: an empty option value can't be submitted, so
    // it must fall through to the unresolved path rather than be silently dropped.
    const fromOptions = valuesByLabel.get(tag.value)?.shift()
    if (fromOptions) return { value: fromOptions, label: tag.value }

    // Free-text multiText: the value IS the label.
    if (!hasOptions) return { value: tag.value, label: tag.value }

    // Options miss on a name/value tag: keep an already email-shaped value
    // (freshly-typed guest); otherwise display-only until options refetch.
    if (tag.value.includes('@')) return { value: tag.value, label: tag.value }
    return { value: '', label: tag.value, unresolved: true }
  })
}

/**
 * The values to submit for a multiText edit. Drops `unresolved` tokens (and any
 * empty value) so a display name is never written into `attendees[].email`; the
 * token stays visible via its label until the edit-options query resolves it.
 */
export function resolveMultiTextSubmitValues(items: ReadonlyArray<MultiTextItem>): Array<string> {
  return items.filter((item) => !item.unresolved && item.value).map((item) => item.value)
}
